import {
  computeLaserTimeMs,
  getBandBounds,
  getFormulasFromBands,
  getSizesFromBands,
  LaserTimeOptions,
  ShootTimeBand,
} from "portal/utils/shootTime";
import { Formula } from "protos/almanac/almanac";
import { ShootTimeXScale, ShootTimeYScale } from "./layout";

const MIN_SAMPLE_STEP_MM = 0.1;
export const MAX_SAMPLE_STEP_MM = 1;

export interface CurvePoint {
  x: number;
  y: number;
}

/** ~1 sample every 2px of plot width, clamped for accuracy vs performance. */
export const getSampleStepMm = (
  maxRadiusMm: number,
  plotWidth: number
): number =>
  Math.min(
    MAX_SAMPLE_STEP_MM,
    Math.max(MIN_SAMPLE_STEP_MM, maxRadiusMm / Math.max(plotWidth * 2, 1))
  );

/**
 * Sample each size band with its own formula so path segments do not chord
 * across discontinuities at thresholds.
 */
export const buildBandPointGroups = (
  sizes: readonly number[],
  formulas: readonly Formula[],
  maxRadiusMm: number,
  stepMm: number,
  options: LaserTimeOptions
): CurvePoint[][] => {
  const bounds = getBandBounds(sizes, maxRadiusMm);

  return bounds.map(({ lower, upper }, bandIndex) => {
    const formula = formulas[bandIndex];
    if (!formula) {
      return [];
    }

    const points: CurvePoint[] = [];
    for (let x = lower; x <= upper; x += stepMm) {
      points.push({
        x,
        y: computeLaserTimeMs(formula, x, options),
      });
    }
    const endY = computeLaserTimeMs(formula, upper, options);
    if (points.length === 0 || points.at(-1)!.x !== upper) {
      points.push({ x: upper, y: endY });
    }
    return points;
  });
};

/** Sample each band's formula across the plot, one point group per band. */
export const sampleBandPointGroups = (
  bands: readonly ShootTimeBand[],
  maxRadiusMm: number,
  plotWidth: number,
  options: LaserTimeOptions
): CurvePoint[][] =>
  buildBandPointGroups(
    getSizesFromBands(bands),
    getFormulasFromBands(bands),
    maxRadiusMm,
    getSampleStepMm(maxRadiusMm, plotWidth),
    options
  );

export const buildPoints = (
  sizes: readonly number[],
  formulas: readonly Formula[],
  maxRadiusMm: number,
  options: LaserTimeOptions,
  stepMm: number = MAX_SAMPLE_STEP_MM
): CurvePoint[] =>
  buildBandPointGroups(sizes, formulas, maxRadiusMm, stepMm, options).flat();

export const pointsToSvgPath = (
  points: readonly CurvePoint[],
  xScale: ShootTimeXScale,
  yScale: ShootTimeYScale
): string =>
  points
    .map((point, index) => {
      const pathCmd = index === 0 ? "M" : "L";
      return `${pathCmd}${xScale(point.x)},${yScale(point.y)}`;
    })
    .join(" ");

/** Closed area between an upper and lower curve over the same x range. */
export const buildEnvelopeAreaPath = (
  upper: readonly CurvePoint[],
  lower: readonly CurvePoint[],
  xScale: ShootTimeXScale,
  yScale: ShootTimeYScale
): string => {
  if (upper.length === 0 || lower.length === 0) {
    return "";
  }
  const forward = pointsToSvgPath(upper, xScale, yScale);
  // Only the first command may be an "M", so walk the lower edge back as lines.
  const backward = [...lower]
    .reverse()
    .map((point) => `L${xScale(point.x)},${yScale(point.y)}`)
    .join(" ");
  return `${forward} ${backward} Z`;
};
